import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  conversationControllerGetConversationQueryOptions,
  conversationControllerListConversationsQueryOptions,
} from 'korint-sdk';
import { useAuth } from 'korint-sdk/react';
import { SparklesIcon } from 'lucide-react';
import { VisuallyHidden } from 'react-aria';
import { Button } from '@folio/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@folio/components/ui/sheet';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { ConversationThread } from '@folio/modules/ConversationModule/ConversationThread';
import { NewConversationForm } from '@folio/modules/ConversationModule/NewConversationForm';
import { FloatingChatHeader } from './FloatingChatHeader';
import { useAIChatAnalytics } from './useAIChatAnalytics';

function useUrlContext() {
  const { pathname } = useLocation();

  return useMemo(() => {
    const segments = pathname.split('/').filter(Boolean);
    const context: Record<string, string> = {};

    for (const segment of segments) {
      if (segment.startsWith('customer_')) context.customerId = segment;
      else if (segment.startsWith('policy_')) context.policyId = segment;
      else if (segment.startsWith('branch_')) context.branchId = segment;
    }

    return context;
  }, [pathname]);
}

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export function FloatingChatModule() {
  const { authStatus } = useAuth();
  const { pathname } = useLocation();

  if (authStatus !== 'authenticated' || pathname.includes('/conversations')) return null;

  return <FloatingChatBubble />;
}

function FloatingChatBubble() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const urlContext = useUrlContext();
  const analytics = useAIChatAnalytics();

  const { can } = useUserPermissions({ actions: ['CREATE_CONVERSATION'] });

  const [open, setOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [initialMessage, setInitialMessage] = useState<string | null>(null);
  const [newConversationKey, setNewConversationKey] = useState(0);

  const { data: conversationsList } = useQuery({
    ...conversationControllerListConversationsQueryOptions(),
    enabled: hasBeenOpened,
  });

  const { data: conversation } = useQuery({
    ...conversationControllerGetConversationQueryOptions(selectedConversationId!),
    enabled: !!selectedConversationId,
  });

  const handleOpen = useCallback(() => {
    setHasBeenOpened(true);

    const latest = conversationsList?.conversations?.[0];
    if (latest?.updatedAt) {
      const elapsed = Date.now() - new Date(latest.updatedAt).getTime();
      if (elapsed < FIVE_MINUTES_MS) {
        setSelectedConversationId(latest.id);
        setInitialMessage(null);
        setOpen(true);
        analytics.trackChatOpened({
          source: 'floating',
          page_url: pathname,
          resumed_conversation: true,
        });
        return;
      }
    }

    setSelectedConversationId(null);
    setInitialMessage(null);
    setOpen(true);
    analytics.trackChatOpened({
      source: 'floating',
      page_url: pathname,
      resumed_conversation: false,
    });
  }, [conversationsList, analytics, pathname]);

  const handleSelectConversation = useCallback(
    (id: string) => {
      setSelectedConversationId(id);
      setInitialMessage(null);
      analytics.trackConversationSelected({
        source: 'floating',
        conversation_id: id,
      });
    },
    [analytics],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleNewConversation = useCallback(() => {
    setSelectedConversationId(null);
    setInitialMessage(null);
    setNewConversationKey(k => k + 1);
    analytics.trackNewConversationClicked({ source: 'floating' });
  }, [analytics]);

  const handleConversationCreated = useCallback(
    (conversationId: string, message: string) => {
      setInitialMessage(message);
      setSelectedConversationId(conversationId);
      analytics.trackConversationCreated({
        source: 'floating',
        conversation_id: conversationId,
        page_url: pathname,
      });
    },
    [analytics, pathname],
  );

  if (!can.CREATE_CONVERSATION) return null;

  return (
    <>
      {!open && (
        <Button
          onClick={handleOpen}
          size="icon"
          aria-label={t('conversation.openChat')}
          className="fixed bottom-6 right-6 z-50 h-12 w-12 rounded-full shadow-lg"
        >
          <SparklesIcon aria-hidden="true" className="h-5 w-5" />
        </Button>
      )}

      <Sheet
        open={open}
        onOpenChange={value => {
          setOpen(value);
          if (!value) {
            analytics.trackChatClosed({
              source: 'floating',
              conversation_id: selectedConversationId,
            });
          }
        }}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg p-0 flex flex-col [&>button]:hidden"
        >
          <VisuallyHidden>
            <SheetTitle>{t('conversation.newConversation')}</SheetTitle>
          </VisuallyHidden>

          <FloatingChatHeader
            currentTitle={selectedConversationId ? (conversation?.title ?? null) : null}
            conversations={conversationsList?.conversations ?? []}
            onSelectConversation={handleSelectConversation}
            onNewConversation={handleNewConversation}
            onClose={handleClose}
          />

          <div className="flex-1 min-h-0">
            {selectedConversationId ? (
              <ConversationThread
                key={selectedConversationId}
                conversationId={selectedConversationId}
                initialMessage={initialMessage}
                onInitialMessageSent={() => setInitialMessage(null)}
                onNavigate={path => navigate(path)}
                showTitle={false}
                source="floating"
                analytics={analytics}
              />
            ) : (
              <NewConversationForm
                key={newConversationKey}
                onConversationCreated={handleConversationCreated}
                urlContext={urlContext}
                source="floating"
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
